"use client";

import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ResultBanner,
  PrintButton,
  DataReviewedNote,
  PrintFooter,
  trackToolResult,
  type Verdict,
} from "@/components/tools/ui";

/* RIDDOR 2013 (SI 2013/1471). This step-by-step checker reuses the verified
   rules embedded in the live Incident Report builder, kept 100% consistent:
   reg 4 (deaths + specified injuries + over-7-day incapacitation to workers),
   reg 6 (non-workers taken to hospital for treatment), reg 7 + Schedule 2
   (dangerous occurrences), reg 8/9 (occupational diseases), and the
   Schedule 1 reporting procedure.

   Deadlines: death / specified injury / non-worker-hospital / dangerous
   occurrence — notify HSE without delay and report online within 10 days;
   over-7-day incapacitation — report online within 15 days of the accident;
   reportable diseases — report without delay once diagnosed.

   The over-7-day count starts the DAY AFTER the accident and includes
   weekends and rest days. The responsible person (employer / self-employed
   person / person in control of the premises) reports — never the injured
   person. Source: legislation.gov.uk/uksi/2013/1471 and HSE INDG453.
   Guidance only — borderline cases go to HSE. */

const RIDDOR_URL = "https://www.hse.gov.uk/riddor";

/* ── Verified content (kept identical to IncidentReportBuilder) ──────────── */

const SPECIFIED_INJURIES = [
  "A bone fracture — other than to a finger, thumb or toe",
  "Amputation of an arm, hand, finger, thumb, leg, foot or toe",
  "Permanent loss of sight or reduction of sight in one or both eyes",
  "A crush injury to the head or torso causing damage to the brain or internal organs",
  "A serious burn (including scalding) covering more than 10% of the body, or damaging the eyes, respiratory system or other vital organs",
  "Any scalping requiring hospital treatment",
  "Loss of consciousness caused by a head injury or asphyxia",
  "Injury from working in an enclosed space leading to hypothermia / heat illness, or needing resuscitation or 24+ hours in hospital",
];

const DANGEROUS_OCCURRENCE_EXAMPLES = [
  "Collapse, overturning or failure of lifting equipment (crane, hoist, MEWP)",
  "Collapse or partial collapse of a scaffold over 5 m high",
  "Electrical short circuit or overload causing fire or explosion",
  "Unintended collapse of a building or structure under construction",
  "Accidental release of a substance that could cause injury to health",
  "Plant or equipment coming into contact with overhead power lines",
  "Failure of pressure systems, or explosion / fire stopping work for 24+ hours",
];

const REPORTABLE_DISEASES = [
  "Carpal tunnel syndrome (where work involves regular use of percussive or vibrating tools)",
  "Occupational dermatitis",
  "Hand-arm vibration syndrome (HAVS)",
  "Occupational asthma",
  "Tendonitis or tenosynovitis of the hand or forearm (from repetitive work)",
  "Any occupational cancer",
  "Any disease attributed to an occupational exposure to a biological agent",
];

/* ── Decision-tree state ────────────────────────────────────────────────── */

type Who = "worker" | "self-employed" | "non-worker";
type What = "death" | "injury" | "no-injury-incident" | "disease";

type Outcome = "reportable" | "record" | "unsure";

interface Verdict_ {
  outcome: Outcome;
  level: Verdict;
  headline: string;
  /** What to report. */
  what: string;
  /** Who is responsible for reporting. */
  who: string;
  /** The deadline. */
  deadline: string;
}

const WHO_LABEL: Record<Who, string> = {
  worker: "A worker (employee or someone you control)",
  "self-employed": "A self-employed person working here",
  "non-worker": "A member of the public / non-worker",
};

const WHAT_LABEL: Record<What, string> = {
  death: "Someone died",
  injury: "Someone was injured",
  "no-injury-incident": "No injury, but a dangerous incident",
  disease: "An occupational disease was diagnosed",
};

/* Who is the responsible person, by who-was-affected. Never the injured
   person themselves (RIDDOR reg 3 / Schedule 1). */
const RESPONSIBLE: Record<Who, string> = {
  worker:
    "The responsible person — the injured worker's employer. They report, not the injured person.",
  "self-employed":
    "The responsible person — if the self-employed person was working under someone else's control, the person in control of the premises reports; otherwise the self-employed person (or someone acting for them) reports. The injured person doesn't report their own minor cases, but where they are in control they hold the duty.",
  "non-worker":
    "The responsible person — the person in control of the premises where the work was being done. The injured member of the public never reports.",
};

const DEADLINE_WITHOUT_DELAY =
  "Notify HSE without delay, then submit the online report within 10 days of the incident.";
const DEADLINE_OVER_SEVEN =
  "Report online within 15 days of the accident.";
const DEADLINE_DISEASE =
  "Report without delay once the diagnosis is received.";

/* ── Step machine ───────────────────────────────────────────────────────── */

type Step =
  | "who"
  | "what"
  | "specified" // injury branch: was it a specified injury?
  | "overseven" // injury branch: over-7-day incapacitation?
  | "nonworker-hospital" // non-worker injury: taken to hospital for treatment?
  | "dangerous" // no-injury-incident branch: dangerous occurrence picker
  | "disease" // disease branch: which disease + doctor-diagnosed
  | "result";

type YN = "yes" | "no" | "unsure";

interface Answers {
  who?: Who;
  what?: What;
  specified?: YN;
  overSeven?: YN;
  nonWorkerHospital?: YN;
  dangerous?: YN;
  dangerousCategory?: string;
  diseaseDiagnosed?: YN;
  diseaseName?: string;
}

function computeVerdict(a: Answers): Verdict_ {
  const who = a.who!;
  const what = a.what!;
  const responsible = RESPONSIBLE[who];

  // ── Death ───────────────────────────────────────────────────────────
  if (what === "death") {
    return {
      outcome: "reportable",
      level: "veryhigh",
      headline: "Reportable under RIDDOR — a work-related death",
      what:
        "A death arising out of or in connection with work is reportable, whether the person was a worker or a member of the public.",
      who: responsible,
      deadline: DEADLINE_WITHOUT_DELAY,
    };
  }

  // ── Occupational disease ────────────────────────────────────────────
  if (what === "disease") {
    if (a.diseaseDiagnosed === "yes") {
      return {
        outcome: "reportable",
        level: "veryhigh",
        headline: "Reportable under RIDDOR — a diagnosed occupational disease",
        what: `A doctor's diagnosis of ${
          a.diseaseName ? a.diseaseName.toLowerCase() : "a listed work-related disease"
        }, where the work involves the relevant exposure or activity, is reportable.`,
        who:
          "The responsible person — the affected person's employer (or, for the self-employed, the person in control or the self-employed person). Reporting follows a written diagnosis from a doctor.",
        deadline: DEADLINE_DISEASE,
      };
    }
    if (a.diseaseDiagnosed === "no") {
      return {
        outcome: "record",
        level: "low",
        headline: "This doesn't appear to meet the reportable criteria",
        what:
          "A reportable disease must be diagnosed in writing by a doctor and linked to the qualifying work. Suspicion or symptoms alone aren't reportable.",
        who: responsible,
        deadline:
          "Keep an internal record and arrange occupational-health review. Re-check if a doctor later confirms a diagnosis.",
      };
    }
    return {
      outcome: "unsure",
      level: "high",
      headline: "Can't tell yet — confirm the diagnosis",
      what:
        "Reportability turns on whether a doctor has diagnosed a listed disease and linked it to the work.",
      who: responsible,
      deadline:
        "If a doctor has diagnosed a listed disease, report without delay. Check the official guidance or HSE if unsure.",
    };
  }

  // ── No injury, but a dangerous incident (dangerous occurrence) ──────
  if (what === "no-injury-incident") {
    if (a.dangerous === "yes") {
      return {
        outcome: "reportable",
        level: "veryhigh",
        headline: "Reportable under RIDDOR — a listed dangerous occurrence",
        what: `A listed dangerous occurrence${
          a.dangerousCategory ? ` (${a.dangerousCategory.toLowerCase()})` : ""
        } is reportable even though no one was injured.`,
        who:
          "The responsible person — the employer or whoever is in control of the premises where it happened.",
        deadline: DEADLINE_WITHOUT_DELAY,
      };
    }
    if (a.dangerous === "no") {
      return {
        outcome: "record",
        level: "low",
        headline: "This doesn't appear to meet the reportable criteria",
        what:
          "Near misses are generally not reportable unless they match one of the listed dangerous occurrences in Schedule 2.",
        who:
          "The responsible person — the employer or person in control of the work.",
        deadline:
          "Keep an internal record (near-miss log). Acting on the cause now is the cheapest way to stop the version that does hurt someone.",
      };
    }
    return {
      outcome: "unsure",
      level: "high",
      headline: "Can't tell yet — check the dangerous-occurrence list",
      what:
        "Schedule 2 of RIDDOR lists the dangerous occurrences that are reportable with no injury. The examples here aren't the full list.",
      who:
        "The responsible person — the employer or person in control of the work.",
      deadline:
        "If it matches a listed occurrence, report without delay. Check the full list at hse.gov.uk/riddor.",
    };
  }

  // ── Injury ───────────────────────────────────────────────────────────
  // Non-worker injuries: reportable only if taken directly to hospital for treatment.
  if (who === "non-worker") {
    if (a.nonWorkerHospital === "yes") {
      return {
        outcome: "reportable",
        level: "veryhigh",
        headline: "Reportable under RIDDOR — non-worker taken to hospital",
        what:
          "A member of the public (or other non-worker) injured by the work and taken directly from the scene to hospital for treatment is reportable.",
        who: responsible,
        deadline: DEADLINE_WITHOUT_DELAY,
      };
    }
    if (a.nonWorkerHospital === "no") {
      return {
        outcome: "record",
        level: "low",
        headline: "This doesn't appear to meet the reportable criteria",
        what:
          "A non-worker injury is reportable only if they were taken directly to hospital for treatment of the injury. First aid on site, or a precautionary check-up, doesn't trigger a report on its own.",
        who: responsible,
        deadline:
          "Keep an internal record. Re-check if the person is later admitted to hospital for treatment of the injury.",
      };
    }
    return {
      outcome: "unsure",
      level: "high",
      headline: "Can't tell yet — confirm the hospital treatment",
      what:
        "For a non-worker, reportability hinges on whether they were taken to hospital for treatment of the injury.",
      who: responsible,
      deadline:
        "If they were taken to hospital for treatment, report without delay. Check the official guidance if unsure.",
    };
  }

  // Worker / self-employed injury: specified injury, then over-7-day.
  if (a.specified === "yes") {
    return {
      outcome: "reportable",
      level: "veryhigh",
      headline: "Reportable under RIDDOR — a specified injury",
      what:
        "Specified injuries (the reg 4 list — fractures other than fingers/thumbs/toes, amputations, serious burns, loss of sight, crush injuries, and the others) are reportable.",
      who: responsible,
      deadline: DEADLINE_WITHOUT_DELAY,
    };
  }

  // Not a specified injury — fall through to over-7-day.
  if (a.overSeven === "yes") {
    return {
      outcome: "reportable",
      level: "veryhigh",
      headline: "Reportable under RIDDOR — over-7-day incapacitation",
      what:
        "A worker unable to do their normal duties for more than seven consecutive days (not counting the day of the accident, but counting weekends and rest days) is reportable.",
      who: responsible,
      deadline: DEADLINE_OVER_SEVEN,
    };
  }
  if (a.specified === "no" && a.overSeven === "no") {
    return {
      outcome: "record",
      level: "low",
      headline: "This doesn't appear to meet the reportable criteria",
      what:
        "On these answers it isn't a specified injury and the worker wasn't off normal duties for more than seven days, so it doesn't look reportable.",
      who: responsible,
      deadline:
        "Keep an internal record (accident book). If the absence later passes seven days, it becomes reportable — re-check then.",
    };
  }

  // Anything unsure / unanswered in the worker-injury branch.
  return {
    outcome: "unsure",
    level: "high",
    headline: "Can't tell yet — check the detail, or check with HSE",
    what:
      "RIDDOR reportability turns on the detail of the injury and any absence. Some answers are unsure.",
    who: responsible,
    deadline:
      "If in doubt, report without delay and within 10 days — it's safer to check. Use the official guidance at hse.gov.uk/riddor.",
  };
}

/* ── Component ───────────────────────────────────────────────────────────── */

/* Days allowed for the online report, derived from the verdict's deadline text.
   10 days: death / specified injury / non-worker-hospital / dangerous occurrence.
   15 days: over-7-day incapacitation. null: "without delay" (disease) / no fixed window. */
function deadlineDays(deadline: string): number | null {
  if (/within 15 days/.test(deadline)) return 15;
  if (/within 10 days/.test(deadline)) return 10;
  return null;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date.getTime());
  d.setDate(d.getDate() + days);
  return d;
}

const fmtLong = (d: Date) =>
  d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

export function RiddorChecker() {
  const [step, setStep] = useState<Step>("who");
  const [history, setHistory] = useState<Step[]>([]);
  const [a, setA] = useState<Answers>({});
  const [incidentDate, setIncidentDate] = useState("");
  const tracked = useRef(false);

  const go = (next: Step) => {
    setHistory((h) => [...h, step]);
    setStep(next);
  };
  const back = () => {
    setHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      setStep(prev);
      return h.slice(0, -1);
    });
  };
  const restart = () => {
    setA({});
    setHistory([]);
    setStep("who");
    setIncidentDate("");
    tracked.current = false;
  };

  // From the "what happened" answer, route to the right branch.
  const chooseWhat = (what: What) => {
    setA((s) => ({ ...s, what }));
    if (what === "death") return go("result");
    if (what === "disease") return go("disease");
    if (what === "no-injury-incident") return go("dangerous");
    // injury
    if (a.who === "non-worker") return go("nonworker-hospital");
    return go("specified");
  };

  const verdict = useMemo(
    () => (step === "result" ? computeVerdict(a) : null),
    [step, a],
  );

  // Concrete deadline date(s) from the incident date, when reportable and the
  // verdict carries a fixed reporting window (10 or 15 days).
  const deadlineCalc = useMemo(() => {
    if (!verdict || verdict.outcome !== "reportable" || !incidentDate) return null;
    const days = deadlineDays(verdict.deadline);
    if (days === null) return null;
    const parts = incidentDate.split("-").map(Number);
    if (parts.length !== 3 || parts.some((n) => !isFinite(n))) return null;
    const start = new Date(parts[0], parts[1] - 1, parts[2]);
    if (isNaN(start.getTime())) return null;
    return { days, due: addDays(start, days), incident: start };
  }, [verdict, incidentDate]);

  useEffect(() => {
    if (verdict && !tracked.current) {
      tracked.current = true;
      trackToolResult("riddor-checker", { outcome: verdict.outcome });
    }
  }, [verdict]);

  // Rough progress feel for the stepper.
  const STEP_INDEX: Record<Step, number> = {
    who: 1,
    what: 2,
    specified: 3,
    overseven: 3,
    "nonworker-hospital": 3,
    dangerous: 3,
    disease: 3,
    result: 4,
  };
  const totalSteps = 4;
  const current = STEP_INDEX[step];

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      {/* ── Decision tree ─────────────────────────────────────────────── */}
      <div className="card shadow-soft p-6 print-hide">
        {/* Progress */}
        <div className="flex items-center justify-between">
          <span className="text-[0.78rem] font-bold uppercase tracking-wide text-muted">
            {step === "result" ? "Result" : `Step ${current} of ${totalSteps}`}
          </span>
          {history.length > 0 && (
            <button
              type="button"
              onClick={back}
              className="inline-flex items-center gap-1 text-[0.82rem] font-semibold text-muted hover:text-ink"
            >
              ← Back
            </button>
          )}
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-mist">
          <div
            className="h-full rounded-full bg-emerald transition-all"
            style={{ width: `${(current / totalSteps) * 100}%` }}
          />
        </div>

        <div className="mt-6">
          {/* STEP 1 — who */}
          {step === "who" && (
            <Question title="Who was affected?">
              {(Object.keys(WHO_LABEL) as Who[]).map((w) => (
                <ChoiceButton
                  key={w}
                  selected={a.who === w}
                  onClick={() => {
                    setA((s) => ({ ...s, who: w }));
                    go("what");
                  }}
                >
                  {WHO_LABEL[w]}
                </ChoiceButton>
              ))}
            </Question>
          )}

          {/* STEP 2 — what */}
          {step === "what" && (
            <Question title="What happened?">
              {(Object.keys(WHAT_LABEL) as What[]).map((w) => (
                <ChoiceButton key={w} onClick={() => chooseWhat(w)}>
                  {WHAT_LABEL[w]}
                </ChoiceButton>
              ))}
            </Question>
          )}

          {/* STEP 3 — worker/self-employed injury: specified injury? */}
          {step === "specified" && (
            <Question title="Was it a specified injury?">
              <DetailList intro="Specified injuries (reg 4) include:" items={SPECIFIED_INJURIES} />
              <YesNoUnsure
                question="Was it a specified injury?"
                onYes={() => {
                  setA((s) => ({ ...s, specified: "yes" }));
                  go("result");
                }}
                onNo={() => {
                  setA((s) => ({ ...s, specified: "no" }));
                  go("overseven");
                }}
                onUnsure={() => {
                  setA((s) => ({ ...s, specified: "unsure" }));
                  go("result");
                }}
              />
            </Question>
          )}

          {/* STEP 3 — over-7-day incapacitation? */}
          {step === "overseven" && (
            <Question title="Was the worker unable to do their normal duties for more than 7 consecutive days?">
              <div className="rounded-lg bg-mist/70 px-3.5 py-2.5 text-[0.85rem] leading-relaxed text-body">
                Count the days from the <strong>day after the accident</strong>, and include weekends and
                rest days. The day of the accident itself doesn&apos;t count. If the worker was off (or on
                restricted duties) for 8 days or more, the answer is yes.
              </div>
              <YesNoUnsure
                question="Was the worker unable to do their normal duties for more than 7 consecutive days?"
                onYes={() => {
                  setA((s) => ({ ...s, overSeven: "yes" }));
                  go("result");
                }}
                onNo={() => {
                  setA((s) => ({ ...s, overSeven: "no" }));
                  go("result");
                }}
                onUnsure={() => {
                  setA((s) => ({ ...s, overSeven: "unsure" }));
                  go("result");
                }}
              />
            </Question>
          )}

          {/* STEP 3 — non-worker taken to hospital? */}
          {step === "nonworker-hospital" && (
            <Question title="Was the non-worker taken directly from the scene to hospital for treatment of the injury?">
              <div className="rounded-lg bg-mist/70 px-3.5 py-2.5 text-[0.85rem] leading-relaxed text-body">
                This means taken to hospital for <strong>treatment</strong> of the injury — not a
                precautionary check-up, and not first aid given on site.
              </div>
              <YesNoUnsure
                question="Was the non-worker taken directly from the scene to hospital for treatment of the injury?"
                onYes={() => {
                  setA((s) => ({ ...s, nonWorkerHospital: "yes" }));
                  go("result");
                }}
                onNo={() => {
                  setA((s) => ({ ...s, nonWorkerHospital: "no" }));
                  go("result");
                }}
                onUnsure={() => {
                  setA((s) => ({ ...s, nonWorkerHospital: "unsure" }));
                  go("result");
                }}
              />
            </Question>
          )}

          {/* STEP 3 — dangerous occurrence picker */}
          {step === "dangerous" && (
            <Question title="Does it match one of the listed dangerous occurrences?">
              <p className="text-[0.85rem] leading-relaxed text-muted">
                These are common examples from Schedule 2 — pick the closest, or choose &ldquo;none of
                these&rdquo;. The full list is longer; check it at{" "}
                <a href={RIDDOR_URL} rel="noopener" target="_blank" className="font-semibold underline">
                  hse.gov.uk/riddor
                </a>
                .
              </p>
              <div className="space-y-2">
                {DANGEROUS_OCCURRENCE_EXAMPLES.map((d) => (
                  <ChoiceButton
                    key={d}
                    onClick={() => {
                      setA((s) => ({ ...s, dangerous: "yes", dangerousCategory: d }));
                      go("result");
                    }}
                  >
                    {d}
                  </ChoiceButton>
                ))}
                <ChoiceButton
                  onClick={() => {
                    setA((s) => ({ ...s, dangerous: "no", dangerousCategory: undefined }));
                    go("result");
                  }}
                >
                  None of these — no one was hurt
                </ChoiceButton>
                <ChoiceButton
                  onClick={() => {
                    setA((s) => ({ ...s, dangerous: "unsure", dangerousCategory: undefined }));
                    go("result");
                  }}
                >
                  Not sure — it might be on the full list
                </ChoiceButton>
              </div>
            </Question>
          )}

          {/* STEP 3 — occupational disease */}
          {step === "disease" && (
            <Question title="Which occupational disease, and is it diagnosed by a doctor?">
              <p className="text-[0.85rem] leading-relaxed text-muted">
                Reportable diseases must be <strong>diagnosed in writing by a doctor</strong> and linked to
                the work that causes them. Pick the closest, then confirm the diagnosis.
              </p>
              <DetailList intro="Reportable occupational diseases include:" items={REPORTABLE_DISEASES} />
              <div className="space-y-2">
                {["Carpal tunnel syndrome", "Occupational dermatitis", "HAVS", "Occupational asthma", "Tendonitis / tenosynovitis", "Occupational cancer", "Another listed disease"].map((d) => (
                  <ChoiceButton
                    key={d}
                    selected={a.diseaseName === d}
                    onClick={() => setA((s) => ({ ...s, diseaseName: d }))}
                  >
                    {d}
                  </ChoiceButton>
                ))}
              </div>
              {a.diseaseName && (
                <div className="mt-2">
                  <div className="mb-2 text-[0.85rem] font-semibold text-ink">
                    Has a doctor diagnosed this in writing and linked it to the work?
                  </div>
                  <YesNoUnsure
                    question="Has a doctor diagnosed this in writing and linked it to the work?"
                    onYes={() => {
                      setA((s) => ({ ...s, diseaseDiagnosed: "yes" }));
                      go("result");
                    }}
                    onNo={() => {
                      setA((s) => ({ ...s, diseaseDiagnosed: "no" }));
                      go("result");
                    }}
                    onUnsure={() => {
                      setA((s) => ({ ...s, diseaseDiagnosed: "unsure" }));
                      go("result");
                    }}
                  />
                </div>
              )}
            </Question>
          )}

          {/* STEP 4 — result */}
          {step === "result" && verdict && (
            <div>
              <ResultBanner level={verdict.level} title={verdict.headline}>
                {verdict.outcome === "reportable" ? (
                  <span>Report it — see the details and deadline below.</span>
                ) : verdict.outcome === "unsure" ? (
                  <span>Borderline — don&apos;t treat this as a definitive answer.</span>
                ) : (
                  <span>
                    This doesn&apos;t appear to meet the reportable criteria — keep an internal record
                    (accident book) and check the official guidance if in doubt.
                  </span>
                )}
              </ResultBanner>

              <dl className="mt-4 space-y-3 text-sm">
                <VerdictLine term="What to report">{verdict.what}</VerdictLine>
                <VerdictLine term="Who reports">{verdict.who}</VerdictLine>
                <VerdictLine term="Deadline">{verdict.deadline}</VerdictLine>
              </dl>

              {verdict.outcome === "reportable" && deadlineDays(verdict.deadline) !== null && (
                <div className="mt-4 rounded-xl border border-line bg-mist/60 px-4 py-3">
                  <label className="block text-[0.85rem] font-semibold text-ink">
                    Work out the deadline date
                  </label>
                  <p className="mt-0.5 text-[0.82rem] text-muted">
                    Enter the date of the incident to see the date the online report is due.
                  </p>
                  <input
                    type="date"
                    value={incidentDate}
                    onChange={(e) => setIncidentDate(e.target.value)}
                    className="mt-2 rounded-lg border border-line-2 bg-white px-3.5 py-2.5 text-[0.95rem] text-ink focus:border-emerald focus:outline-none focus:ring-2 focus:ring-emerald/25"
                  />
                  {deadlineCalc && (
                    <p className="mt-2 text-[0.9rem] leading-relaxed text-body" aria-live="polite">
                      Report online within {deadlineCalc.days} days —{" "}
                      <strong className="text-ink">by {fmtLong(deadlineCalc.due)}</strong>.
                      {deadlineDays(verdict.deadline) === 10 && " Notify HSE without delay in the meantime."}
                    </p>
                  )}
                </div>
              )}

              {verdict.outcome === "reportable" && (
                <p className="mt-4 text-[0.85rem] leading-relaxed text-body">
                  Make the report at{" "}
                  <a href={RIDDOR_URL} rel="noopener" target="_blank" className="font-semibold underline">
                    hse.gov.uk/riddor
                  </a>
                  . The injured person never makes the report themselves.
                </p>
              )}
              {verdict.outcome === "unsure" && (
                <p className="mt-4 text-[0.85rem] leading-relaxed text-body">
                  Check the official guidance at{" "}
                  <a href={RIDDOR_URL} rel="noopener" target="_blank" className="font-semibold underline">
                    hse.gov.uk/riddor
                  </a>
                  , or call the HSE incident reporting line for fatal and major incidents. If in doubt,
                  it&apos;s safer to report.
                </p>
              )}
              {verdict.outcome === "record" && (
                <p className="mt-4 text-[0.85rem] leading-relaxed text-body">
                  This isn&apos;t a legal ruling. If anything changes — the injury worsens, an absence
                  passes seven days, or a doctor later diagnoses a listed disease — re-check. Confirm
                  borderline cases at{" "}
                  <a href={RIDDOR_URL} rel="noopener" target="_blank" className="font-semibold underline">
                    hse.gov.uk/riddor
                  </a>
                  .
                </p>
              )}

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={back}
                  className="inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist"
                >
                  ← Change last answer
                </button>
                <button
                  type="button"
                  onClick={restart}
                  className="inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist"
                >
                  Start again
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Cross-link to the live incident report builder */}
        <div className="mt-6 rounded-xl border border-line bg-mist/60 px-4 py-3 text-sm text-body">
          Need the full incident report?{" "}
          <Link href="/tools/incident-report/" className="font-semibold text-emerald-deep underline">
            Use the incident &amp; near-miss report builder
          </Link>{" "}
          to record the facts, root cause and corrective actions for the H&amp;S file.
        </div>
      </div>

      {/* ── Printable decision record ─────────────────────────────────── */}
      <div className="space-y-4">
        {verdict ? (
          <>
            <div className="print-area card shadow-soft p-6" id="riddor-record">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                RIDDOR decision record
              </div>
              <h2 className="mt-1 text-lg">
                {new Date().toLocaleString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </h2>

              <div className="mt-4">
                <ResultBanner level={verdict.level} title={verdict.headline} />
              </div>

              <div className="mt-4">
                <RecordHeading>Answers given</RecordHeading>
                <dl className="mt-1.5 space-y-1.5 text-sm">
                  {a.who && <RecordLine term="Who was affected">{WHO_LABEL[a.who]}</RecordLine>}
                  {a.what && <RecordLine term="What happened">{WHAT_LABEL[a.what]}</RecordLine>}
                  {a.specified && a.what === "injury" && a.who !== "non-worker" && (
                    <RecordLine term="Specified injury">{cap(a.specified)}</RecordLine>
                  )}
                  {a.overSeven && (
                    <RecordLine term="Over 7-day incapacitation">{cap(a.overSeven)}</RecordLine>
                  )}
                  {a.nonWorkerHospital && (
                    <RecordLine term="Taken to hospital for treatment">{cap(a.nonWorkerHospital)}</RecordLine>
                  )}
                  {a.what === "no-injury-incident" && a.dangerous && (
                    <RecordLine term="Dangerous occurrence">
                      {a.dangerous === "yes"
                        ? a.dangerousCategory || "Yes — listed occurrence"
                        : cap(a.dangerous)}
                    </RecordLine>
                  )}
                  {a.what === "disease" && a.diseaseName && (
                    <RecordLine term="Occupational disease">{a.diseaseName}</RecordLine>
                  )}
                  {a.what === "disease" && a.diseaseDiagnosed && (
                    <RecordLine term="Diagnosed by a doctor">{cap(a.diseaseDiagnosed)}</RecordLine>
                  )}
                </dl>
              </div>

              <div className="mt-4">
                <RecordHeading>Outcome</RecordHeading>
                <dl className="mt-1.5 space-y-1.5 text-sm">
                  <RecordLine term="What to report">{verdict.what}</RecordLine>
                  <RecordLine term="Who reports">{verdict.who}</RecordLine>
                  <RecordLine term="Deadline">{verdict.deadline}</RecordLine>
                  {deadlineCalc && (
                    <>
                      <RecordLine term="Incident date">{fmtLong(deadlineCalc.incident)}</RecordLine>
                      <RecordLine term="Report due by">
                        {fmtLong(deadlineCalc.due)} (within {deadlineCalc.days} days)
                      </RecordLine>
                    </>
                  )}
                </dl>
              </div>

              <DataReviewedNote date="June 2026">
                Decision logic follows RIDDOR 2013 as published at hse.gov.uk/riddor. Reporting rules are
                under review — confirm borderline cases against the current guidance.
              </DataReviewedNote>

              <PrintFooter toolName="RIDDOR checker" />
            </div>
            <div className="flex justify-end">
              <PrintButton tool="riddor-checker" label="Print / save record as PDF" />
            </div>
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Answer the questions to get a RIDDOR verdict — what to report, who reports it and the
            deadline — plus a printable decision record for the H&amp;S file.
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Small presentational helpers ─────────────────────────────────────────── */

function cap(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function Question({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-xl leading-snug">{title}</h2>
      <div className="mt-4 space-y-3">{children}</div>
    </div>
  );
}

function ChoiceButton({
  children,
  onClick,
  selected,
}: {
  children: React.ReactNode;
  onClick: () => void;
  selected?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left text-[0.92rem] font-semibold leading-snug transition-colors ${
        selected
          ? "border-emerald bg-emerald-tint text-emerald-deep"
          : "border-line-2 bg-white text-ink hover:border-ink hover:bg-mist"
      }`}
    >
      <span>{children}</span>
      <span className="shrink-0 text-muted">→</span>
    </button>
  );
}

function YesNoUnsure({
  question,
  onYes,
  onNo,
  onUnsure,
}: {
  /** The question being answered — gives screen readers context for the
      otherwise-identical Yes/No buttons repeated through the wizard. */
  question: string;
  onYes: () => void;
  onNo: () => void;
  onUnsure: () => void;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-3" role="group" aria-label={question}>
      <button
        type="button"
        onClick={onYes}
        className="rounded-xl border border-line-2 bg-white px-4 py-3 text-sm font-semibold text-ink transition-colors hover:border-ink hover:bg-mist"
      >
        Yes
      </button>
      <button
        type="button"
        onClick={onNo}
        className="rounded-xl border border-line-2 bg-white px-4 py-3 text-sm font-semibold text-ink transition-colors hover:border-ink hover:bg-mist"
      >
        No
      </button>
      <button
        type="button"
        onClick={onUnsure}
        className="rounded-xl border border-line-2 bg-white px-4 py-3 text-sm font-semibold text-muted transition-colors hover:border-ink hover:text-ink"
      >
        Not sure
      </button>
    </div>
  );
}

function DetailList({ intro, items }: { intro: string; items: string[] }) {
  return (
    <details className="rounded-lg bg-mist/70 px-3.5 py-2.5 text-[0.82rem] text-body">
      <summary className="cursor-pointer font-semibold text-ink">{intro}</summary>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-muted">
        {items.map((it) => (
          <li key={it}>{it}</li>
        ))}
      </ul>
    </details>
  );
}

function VerdictLine({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[0.74rem] font-semibold uppercase tracking-wide text-muted">{term}</dt>
      <dd className="mt-0.5 leading-relaxed text-body">{children}</dd>
    </div>
  );
}

function RecordHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">{children}</div>
  );
}

function RecordLine({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
      <dt className="shrink-0 font-semibold text-ink sm:w-44">{term}</dt>
      <dd className="text-body">{children}</dd>
    </div>
  );
}
